import React from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { FilterChip } from "./FilterChip";
import { useThemeColor } from "@/hooks/useThemeColor";
import {
  IPlaceFilterCriteria,
  Contact,
  ContactModel,
  GroupModel,
  MemoryModel,
} from "common247";

export interface IPlaceFilterComponentProps {
  criteria: IPlaceFilterCriteria;
  onChange: (updated: IPlaceFilterCriteria) => void;
  allContacts?: (Contact | ContactModel)[];
  allGroups?: GroupModel[];
  allMemories?: MemoryModel[];
}

const RATINGS = [
  { rating: 1, label: "Any Rating" },
  { rating: 3, label: "3+ Stars" },
  { rating: 4, label: "4+ Stars" },
  { rating: 5, label: "5 Stars" },
];

/**
 * Filter controls specific to Place entities.
 */
export function PlaceFilter({
  criteria,
  onChange,
  allContacts = [],
  allGroups = [],
  allMemories = [],
}: IPlaceFilterComponentProps): React.JSX.Element {
  const textMuted = useThemeColor({}, "textMuted");

  const minRating = criteria.minRating || 1;
  const currentContactIds = criteria.contactIds || [];
  const currentGroupIds = criteria.groupIds || [];
  const currentMemoryIds = criteria.memoryIds || [];

  const handleContactToggle = (contactId: string): void => {
    const updated = currentContactIds.includes(contactId)
      ? currentContactIds.filter((id) => id !== contactId)
      : [...currentContactIds, contactId];
    onChange({ ...criteria, contactIds: updated.length > 0 ? updated : undefined });
  };

  const handleGroupToggle = (groupId: string): void => {
    const updated = currentGroupIds.includes(groupId)
      ? currentGroupIds.filter((id) => id !== groupId)
      : [...currentGroupIds, groupId];
    onChange({ ...criteria, groupIds: updated.length > 0 ? updated : undefined });
  };

  const handleMemoryToggle = (memoryId: string): void => {
    const updated = currentMemoryIds.includes(memoryId)
      ? currentMemoryIds.filter((id) => id !== memoryId)
      : [...currentMemoryIds, memoryId];
    onChange({ ...criteria, memoryIds: updated.length > 0 ? updated : undefined });
  };

  return (
    <View style={styles.container}>
      {/* Minimum Rating */}
      <View style={styles.section}>
        <ThemedText style={[styles.label, { color: textMuted }]}>Rating</ThemedText>
        <View style={styles.chipsWrap}>
          {RATINGS.map((item) => (
            <FilterChip
              key={item.rating}
              label={item.label}
              icon="star"
              selected={minRating === item.rating}
              onPress={() =>
                onChange({
                  ...criteria,
                  minRating: item.rating === 1 ? undefined : item.rating,
                })
              }
              compact
            />
          ))}
        </View>
      </View>

      {/* Linked Memories */}
      {allMemories.length > 0 && (
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: textMuted }]}>Linked Memories</ThemedText>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.horizScroll}>
            {allMemories.map((m) => (
              <FilterChip
                key={m.id}
                label={m.title}
                icon="book-outline"
                selected={currentMemoryIds.includes(m.id)}
                onPress={() => handleMemoryToggle(m.id)}
                compact
              />
            ))}
          </ScrollView>
        </View>
      )}

      {/* Linked Contacts */}
      {allContacts.length > 0 && (
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: textMuted }]}>Linked Contacts</ThemedText>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.horizScroll}>
            {allContacts.map((c) => (
              <FilterChip
                key={c.id}
                label={c.displayName}
                icon="person-outline"
                selected={currentContactIds.includes(c.id || "")}
                onPress={() => c.id && handleContactToggle(c.id)}
                compact
              />
            ))}
          </ScrollView>
        </View>
      )}

      {/* Linked Groups */}
      {allGroups.length > 0 && (
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: textMuted }]}>Linked Groups</ThemedText>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.horizScroll}>
            {allGroups.map((g) => (
              <FilterChip
                key={g.id}
                label={g.name}
                icon="people-outline"
                selected={currentGroupIds.includes(g.id)}
                onPress={() => handleGroupToggle(g.id)}
                compact
              />
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: 4,
  },
  section: {
    marginBottom: 8,
  },
  label: {
    fontSize: 12,
    fontWeight: "600",
    marginBottom: 4,
  },
  chipsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  horizScroll: {
    flexDirection: "row",
  },
});
